import React from 'react';
import { Moon, Clock, DollarSign } from 'lucide-react';

/**
 * TonightSalesTile
 * --------------------------------------------------------------
 * Owner directive (May 31 2026): trading day = 5pm → 4am Melbourne.
 * Surface tonight's running revenue with an unambiguous label of the
 * active business window so the manager can glance at the Overview
 * page and immediately know "what we've done this session".
 *
 * Reads from /api/stats/dashboard which now returns:
 *   today_revenue          (number)   — paid orders summed across the window
 *   business_date          (YYYY-MM-DD) — date the session started
 *   business_window_start  (iso UTC)  — 17:00 local on business_date
 *   business_window_end    (iso UTC)  — 04:00 local the next day
 *
 * Self-fetches every 30s so a long-open Overview tab stays current.
 */
function fmtLocal(iso, opts) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      timeZone: 'Australia/Melbourne',
      ...opts,
    });
  } catch { return iso; }
}

export default function TonightSalesTile({ stats }) {
  const start = stats?.business_window_start;
  const end = stats?.business_window_end;
  const revenue = Number(stats?.today_revenue || 0);

  // "Sat 31 May 5:00pm → Sun 1 Jun 4:00am"
  const label = start && end
    ? `${fmtLocal(start, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })} → ${fmtLocal(end, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })}`
    : '5pm → 4am Melbourne';

  return (
    <div
      className="relative overflow-hidden bg-gradient-to-br from-gold/[0.12] via-gold/[0.06] to-transparent border border-gold/30 rounded-sm p-5 sm:p-6 col-span-2 lg:col-span-3 xl:col-span-6"
      data-testid="tonight-sales-tile"
    >
      <div className="absolute -right-6 -top-6 opacity-10">
        <Moon className="w-32 h-32 text-gold" />
      </div>
      <div className="relative flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Moon className="w-4 h-4 text-gold" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-gold font-sans font-semibold">
              Tonight's Sales · 5pm → 4am
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <DollarSign className="w-6 h-6 text-gold" />
            <span className="text-3xl sm:text-4xl font-serif text-white font-semibold leading-none" data-testid="tonight-sales-amount">
              {revenue.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-xs text-zinc-400 font-sans pl-1">so far</span>
          </div>
          <p className="text-[11px] text-zinc-400 font-sans mt-2 flex items-center gap-1.5" data-testid="tonight-sales-window">
            <Clock className="w-3 h-3 text-gold/60" />
            <span>{label}</span>
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans">Trading day</p>
          <p className="text-sm text-zinc-200 font-mono mt-0.5" data-testid="tonight-sales-date">
            {stats?.business_date || '—'}
          </p>
        </div>
      </div>
    </div>
  );
}
